import {useCallback, useEffect} from 'react'
import {useAppSelector} from '../app/store';
import {DEFAULT_SESSION, Session, SessionState} from "../app/slices/sessionSlice";
import {useGetMeQuery} from "../apis/CurrentUser.api";
import { useNavigate } from 'react-router';
import {useLoginUserMutation, useRegisterUserMutation, useLogoutUserMutation, useRefreshMutation} from "../apis/Auth.api";
import { useCookie } from "react-use";
import { useDynamicSnackbar } from '../share';
import {LoginRequest, RegisterRequest} from "../types/auth";
import { t } from "@lingui/core/macro";

const MILLISECONDS_UNTIL_EXPIRY_CHECK = 10 * 1000 // check expiry every 10 seconds
const REMAINING_TOKEN_EXPIRY_TIME_ALLOWED = 60 * 1000 // 1 minute before token should be refreshed

const getStateSession = (): SessionState | null => {
    return useAppSelector((state) => (state.session as SessionState) || null);
}

export const useStateSession = (): Session => {
    return getStateSession()?.session || DEFAULT_SESSION;
}

const handleError = (actionError: any, openFailSnackbar) => {
    if (Array.isArray(actionError?.data?.error)) {
        actionError.data.error.forEach((el: any) => {
            openFailSnackbar(el.message)
        });
    } else if (actionError?.data?.message) {
        openFailSnackbar(actionError.data.message);
    } else {
        console.log('Unknown error', actionError)
        openFailSnackbar(actionError.data.message);
    }
}

export default function useAuth() {

    const [loginUserMutation, {isLoading: loginLoading, isError: loginIsError, error: loginError}] = useLoginUserMutation()
    const [registerUserMutation, {isLoading: registerLoading, isError: registerIsError, error: registerError}] = useRegisterUserMutation()
    const [logoutUserMutation, {isLoading: logoutLoading}] = useLogoutUserMutation()
    const [refreshMutation, {isLoading: refreshLoading}] = useRefreshMutation()
    const [_, setCookie, removeCookie] = useCookie('logged_in');
    const navigate = useNavigate();
    const {openSuccessSnackbar, openFailSnackbar} = useDynamicSnackbar();

    const session = useStateSession()

    const isAuthenticated = !!session.userId;

    const handleLogin = useCallback(async (login: LoginRequest, afterLoginPath: string) => {
        try {
            const status = await loginUserMutation(login).unwrap()
            setCookie(true)
            if (status?.access_token) {
                if (afterLoginPath) {
                    navigate(afterLoginPath);
                }
                openSuccessSnackbar(t`You successfully logged in`);
            } else if (loginIsError) {
                handleError(loginError, openFailSnackbar)
            }
        } catch (e) {
            handleError(e, openFailSnackbar)
        }
    }, [loginUserMutation, setCookie, openSuccessSnackbar, openFailSnackbar])

    const handleRegister = useCallback(async (register: RegisterRequest) => {
        try {
            const response = await registerUserMutation(register).unwrap()
            if (registerIsError) {
                handleError(registerError, openFailSnackbar)
            } else {
                navigate('/activation');
                openSuccessSnackbar(response.message);
            }
        } catch (e) {
            handleError(e, openFailSnackbar)
        }
    }, [registerUserMutation, openSuccessSnackbar, openFailSnackbar])

    const handleLogout = useCallback(async () => {
        try {
            removeCookie()
            navigate('/login');
            await logoutUserMutation().unwrap()
            removeCookie()
        } catch (e) {
            handleError(e, openFailSnackbar)
        }
    }, [logoutUserMutation, removeCookie])

    const handleRefresh = useCallback(async (scopeGroupId: ID, reset: boolean = false) => {
        try {
            const status = await refreshMutation({scope_group: scopeGroupId, reset}).unwrap()

            if (status?.access_token) {
                // refresh page
                // window.location.reload()
            }
        } catch (e) {
            handleError(e, openFailSnackbar)
        }
    }, [])

    return {
        session, isAuthenticated,
        handleLogin, loginLoading,
        handleRegister, registerLoading,
        handleLogout, logoutLoading,
        handleRefresh, refreshLoading,
    }
}

export const useAuthCheck = () => {

    const session = useStateSession();
    const [cookies] = useCookies(['logged_in']);
    const getMeQuery = useGetMeQuery();


    const refreshIfNecessary = useCallback(async () => {

        const isExpiringSoon = () => {
            if (session?.expiresOnUTC) {
                const expireTimeMS = session.expiresOnUTC * 1000
                const currentTimeMS = Date.now()

                console.log("isExpiringSoon", expireTimeMS - currentTimeMS, REMAINING_TOKEN_EXPIRY_TIME_ALLOWED)

                return expireTimeMS - currentTimeMS <= REMAINING_TOKEN_EXPIRY_TIME_ALLOWED
            }

            return true
        }

        if (!session || isExpiringSoon()) {
            await getMeQuery();
        }
    }, [session, getMeQuery])

    useEffect(() => {
        if (cookies.logged_in) {
            refreshIfNecessary()
            let intervalId: NodeJS.Timeout | undefined = undefined

            if (session) {
                // if the access token is set, we want to check its expiry on some interval
                intervalId = setInterval(() => {
                    refreshIfNecessary()
                }, MILLISECONDS_UNTIL_EXPIRY_CHECK)
            }
            return () => {
                if (intervalId) {
                    clearInterval(intervalId)
                }
            }
        }
    }, [refreshIfNecessary, cookies])
}
